import { AdvancedMarker } from '@vis.gl/react-google-maps'
import { GLYPHS } from '../glyphs'
import { dirDuration } from '@/types/structs'
import { convertDistanceToText } from '@/lib/format'
import { appState } from '@/store/appState'
import type { DirectionFor } from '@/types/enums'
import type { LatLng } from '@/types/structs'

/* MarkerId("distance") — the route callout (create_distance_icon.dart +
 * loadDistanceMarkerDescriptor).
 *
 * DELIBERATE DEVIATION (user-approved): the original rasterized the design
 * and rendered it at a FIXED WIDTH of 80px, so the effective text size
 * SHRANK as duration.text got longer ("1 hour 23 mins" -> ~8px). Here the
 * TEXT SIZE is fixed instead and the bubble width follows the content —
 * always readable. Sizes below are the original design scaled by a constant
 * 0.8 (the scale the 80px raster produced for a typical short title).
 *
 * Faithful parts: single-silhouette look (top pointer, body border broken
 * under the triangle base), sharp corners, 1px black border, white fill,
 * black glyph at natural aspect, title bold + distance below-left w500,
 * drop shadow, anchor (0.5, 0.0) — bubble hangs below the route midpoint.
 *
 * Fonts: the canvas asked for "Inter" (unregistered in pubspec -> Flutter
 * fell back to its default); we ship real Inter — the design's intent.
 *
 * Content is read from FFAppState AT RENDER TIME — _updatePolyline mutates
 * model.distance in place (silently) and the same setState batch re-renders
 * this marker, matching the original's bitmap-regeneration timing. */

const S = 0.8 // constant design->screen scale (was dynamic 80/designWidth)

export function DistanceCallout({
  position,
  directionFor,
}: {
  position: LatLng
  directionFor: DirectionFor
}) {
  const model =
    directionFor === 'walk'
      ? appState.walkingDirectionModel
      : directionFor === 'bicycle'
        ? appState.bicyclingDirectionModel
        : appState.drivingDirectionModel

  const duration = dirDuration(model).text
  const distanceText = convertDistanceToText(model.distance?.value ?? null)
  const glyph = GLYPHS[directionFor]

  return (
    <AdvancedMarker position={position} zIndex={4}>
      <div
        data-marker="distance"
        style={{
          width: 'max-content', // width follows content — no 80px cap
          transform: 'translateY(100%)', // anchor (0.5, 0.0)
          filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.25))',
        }}
      >
        {/* Pointer: triangle 16x8 + 2px white strip that interrupts the
            body's top border beneath the base (single-outline look). */}
        <svg
          width="16"
          height="10"
          viewBox="0 0 16 10"
          style={{ display: 'block', margin: '0 auto -2px', position: 'relative', zIndex: 1 }}
        >
          <path d="M0 8 L8 0 L16 8 L16 10 L0 10 Z" fill="#ffffff" />
          <path d="M0 8 L8 0 L16 8" fill="none" stroke="#000000" strokeWidth="1" />
        </svg>

        {/* Body: white, 1px black border, SHARP corners. */}
        <div style={{ background: '#ffffff', border: '1px solid #000000', padding: 6 }}>
          {/* Row 1: glyph (natural aspect, x0.8) + title centred vs glyph. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <svg
              width={glyph.w * S}
              height={glyph.h * S}
              viewBox={glyph.viewBox}
              style={{ flexShrink: 0, display: 'block' }}
            >
              <path d={glyph.path} fill="#000000" />
            </svg>
            <span
              style={{
                fontFamily: "'Inter', sans-serif",
                fontWeight: 700,
                fontSize: 13,
                lineHeight: 1,
                whiteSpace: 'nowrap',
                color: '#000000',
              }}
            >
              {duration}
            </span>
          </div>
          {/* Distance below the whole row, left-aligned. */}
          <div
            style={{
              marginTop: 8,
              fontFamily: "'Inter', sans-serif",
              fontWeight: 500,
              fontSize: 11,
              lineHeight: 1,
              whiteSpace: 'nowrap',
              color: '#000000',
            }}
          >
            {distanceText}
          </div>
        </div>
      </div>
    </AdvancedMarker>
  )
}
